import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useCallback, useContext, useEffect, useReducer, } from "react";
import { useAuthContext } from "./auth-context.js";
const initialState = {
    organizations: [],
    activeOrganization: null,
    isLoading: true,
    error: null,
};
function orgReducer(state, action) {
    switch (action.type) {
        case "LOADING":
            return { ...state, isLoading: true, error: null };
        case "LOADED": {
            const activeOrg = action.activeOrgId
                ? (action.organizations.find((o) => o.id === action.activeOrgId) ?? action.organizations[0])
                : action.organizations[0];
            return {
                organizations: action.organizations,
                activeOrganization: activeOrg ?? null,
                isLoading: false,
                error: null,
            };
        }
        case "SWITCHED":
            return { ...state, activeOrganization: action.activeOrganization };
        case "ERROR":
            return { ...state, isLoading: false, error: action.error };
        default:
            return state;
    }
}
const OrganizationContext = createContext(null);
export function OrganizationProvider({ children, config }) {
    const [state, dispatch] = useReducer(orgReducer, initialState);
    const { isAuthenticated, user } = useAuthContext();
    const fetchOrganizations = useCallback(async () => {
        dispatch({ type: "LOADING" });
        try {
            const response = await fetch(`${config.apiBaseUrl}/v1/users/me/organizations`, {
                credentials: "include",
            });
            if (!response.ok) {
                throw new Error("Failed to fetch organizations");
            }
            const data = await response.json();
            dispatch({
                type: "LOADED",
                organizations: data.organizations,
                activeOrgId: data.activeOrgId,
            });
        }
        catch (error) {
            dispatch({ type: "ERROR", error: error });
        }
    }, [config.apiBaseUrl]);
    useEffect(() => {
        if (isAuthenticated && user) {
            fetchOrganizations();
        }
        else {
            dispatch({ type: "LOADED", organizations: [] });
        }
    }, [isAuthenticated, user, fetchOrganizations]);
    const switchOrganization = useCallback(async (orgId) => {
        try {
            const response = await fetch(`${config.apiBaseUrl}/v1/auth/switch-organization`, {
                method: "POST",
                credentials: "include",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({ organizationId: orgId }),
            });
            if (!response.ok) {
                throw new Error("Failed to switch organization");
            }
            const activeOrg = state.organizations.find((o) => o.id === orgId);
            if (activeOrg) {
                dispatch({ type: "SWITCHED", activeOrganization: activeOrg });
            }
            // Refresh session to get updated claims
            window.location.reload();
        }
        catch (error) {
            dispatch({ type: "ERROR", error: error });
        }
    }, [config.apiBaseUrl, state.organizations]);
    const refreshOrganizations = useCallback(async () => {
        await fetchOrganizations();
    }, [fetchOrganizations]);
    const value = {
        ...state,
        switchOrganization,
        refreshOrganizations,
    };
    return _jsx(OrganizationContext.Provider, { value: value, children: children });
}
export function useOrganizationContext() {
    const context = useContext(OrganizationContext);
    if (!context) {
        throw new Error("useOrganizationContext must be used within an OrganizationProvider");
    }
    return context;
}
//# sourceMappingURL=organization-context.js.map